import React, { useState } from 'react';
import AppDropdown from '../../../../components/AppDropdown';
import { SLAB_TYPE_OPTIONS } from '../api/payHeadsApi';

export default function FormulaBuilderSection({
  availablePayHeads = [],
  percentage = 0,
  onPercentageChange,
  formulas = [],
  onFormulasChange,
  slabs = [],
  onSlabsChange,
}) {
  const [activeTab, setActiveTab] = useState(slabs.length > 0 ? 'slabs' : 'percentage');

  // Toggle selection of a base pay head for formula dependency
  const togglePayHead = (payHeadId) => {
    const exists = formulas.some((f) => f.payheads_id === payHeadId);
    if (exists) {
      onFormulasChange(formulas.filter((f) => f.payheads_id !== payHeadId));
    } else {
      onFormulasChange([...formulas, { payheads_id: payHeadId, formula_type: 1 }]);
    }
  };

  // Add a new empty slab row
  const addSlab = () => {
    const lastTo = slabs.length > 0 ? Number(slabs[slabs.length - 1].amt_to || 0) : 0;
    const newFrom = lastTo > 0 ? lastTo + 1 : 0;
    onSlabsChange([
      ...slabs,
      {
        amt_from: newFrom,
        amt_to: 0,
        slab_type: 1, // Percentage
        value: 0,
      },
    ]);
  };

  // Update a specific field in a slab row
  const updateSlab = (index, field, val) => {
    const updated = [...slabs];
    updated[index] = { ...updated[index], [field]: val };
    onSlabsChange(updated);
  };

  // Remove a slab row
  const removeSlab = (index) => {
    onSlabsChange(slabs.filter((_, i) => i !== index));
  };

  // Selected base pay head names for expression preview
  const selectedHeadNames = formulas
    .map((f) => {
      const match = availablePayHeads.find((h) => h.id === f.payheads_id);
      return match ? match.alias || match.name : f.payhead_alias || f.payhead_name || `ID:${f.payheads_id}`;
    })
    .filter(Boolean);

  return (
    <div className="rounded-2xl border border-primary/20 bg-surface-container-low/50 p-6 space-y-6">
      {/* Header Banner */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/15 pb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-base">functions</span>
            </span>
            <h3 className="text-sm font-bold text-on-surface">Computed Value Configuration</h3>
          </div>
          <p className="mt-0.5 text-xs text-on-surface-variant">
            Define formula dependencies and tiered calculation rules for this pay head.
          </p>
        </div>

        {/* Computation Mode Toggle */}
        <div className="inline-flex rounded-xl bg-surface-container-high p-1 border border-outline-variant/20">
          <button
            type="button"
            onClick={() => setActiveTab('percentage')}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              activeTab === 'percentage'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Percentage Rule
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('slabs')}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              activeTab === 'slabs'
                ? 'bg-primary text-on-primary shadow-sm'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            Progressive Slabs ({slabs.length})
          </button>
        </div>
      </div>

      {/* Mode 1: Percentage of Base Pay Heads */}
      {activeTab === 'percentage' && (
        <div className="space-y-6">
          {/* Rate Input */}
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-center gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right">
              Computation Rate (%)
            </label>
            <div className="flex items-center gap-2 max-w-xs">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={percentage}
                onChange={(e) => onPercentageChange(Number(e.target.value))}
                placeholder="e.g. 12 for 12%"
                className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-3 text-xs font-bold text-on-surface outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary/40"
              />
              <span className="text-xs font-bold text-on-surface-variant">%</span>
            </div>
          </div>

          {/* Base Pay Heads Dependency Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] items-start gap-3">
            <label className="text-xs font-bold text-on-surface-variant sm:text-right pt-2">
              Compute On Pay Heads
            </label>
            <div className="space-y-3">
              {availablePayHeads.length === 0 ? (
                <p className="text-xs text-on-surface-variant italic">
                  No other active pay heads configured yet. Create foundational pay heads (e.g. Basic Salary) first.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-52 overflow-y-auto pr-1">
                  {availablePayHeads.map((h) => {
                    const isSelected = formulas.some((f) => f.payheads_id === h.id);
                    return (
                      <button
                        key={h.id}
                        type="button"
                        onClick={() => togglePayHead(h.id)}
                        className={`flex items-center justify-between rounded-xl border p-2.5 text-left transition ${
                          isSelected
                            ? 'border-primary bg-primary/10 text-primary font-bold shadow-xs'
                            : 'border-outline-variant/20 bg-surface-container-lowest text-on-surface hover:border-outline-variant/50'
                        }`}
                      >
                        <div className="min-w-0 pr-2">
                          <p className="text-xs truncate">{h.name}</p>
                          <p className="text-[10px] text-on-surface-variant uppercase font-mono">
                            {h.alias || h.pay_head_type}
                          </p>
                        </div>
                        <span className="material-symbols-outlined text-sm">
                          {isSelected ? 'check_circle' : 'add_circle'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Expression Preview */}
              {selectedHeadNames.length > 0 && (
                <div className="flex items-center gap-2 rounded-xl bg-primary/5 border border-primary/20 px-3.5 py-2 text-xs text-primary font-mono">
                  <span className="material-symbols-outlined text-sm">calculate</span>
                  <span>
                    Formula: {percentage || 0}% &times; ({selectedHeadNames.join(' + ')})
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mode 2: Progressive Slabs */}
      {activeTab === 'slabs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-on-surface-variant">
              Tiered brackets for bracket-based calculations (e.g. Professional Tax).
            </p>
            <button
              type="button"
              onClick={addSlab}
              className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">add</span>
              <span>Add Slab Bracket</span>
            </button>
          </div>

          {slabs.length === 0 ? (
            <div className="rounded-xl border border-dashed border-outline-variant/40 p-8 text-center bg-surface-container-lowest">
              <span className="material-symbols-outlined text-3xl text-on-surface-variant/40">table_rows</span>
              <p className="mt-1 text-xs font-bold text-on-surface">No Slabs Configured</p>
              <p className="text-[11px] text-on-surface-variant">Click &quot;Add Slab Bracket&quot; to configure amount ranges.</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-outline-variant/20 bg-surface-container-lowest shadow-xs">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="px-3.5 py-2.5">Amount From (₹)</th>
                    <th className="px-3.5 py-2.5">Amount To (₹)</th>
                    <th className="w-40 px-3.5 py-2.5">Slab Type</th>
                    <th className="px-3.5 py-2.5">Value / Rate</th>
                    <th className="w-16 px-3.5 py-2.5 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/15">
                  {slabs.map((slab, idx) => (
                    <tr key={idx} className="hover:bg-surface-container-low/40 transition-colors">
                      <td className="px-3.5 py-2">
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={slab.amt_from}
                          onChange={(e) => updateSlab(idx, 'amt_from', Number(e.target.value))}
                          className="h-8 w-full rounded-md border border-outline-variant/30 bg-surface-container-low px-2.5 text-xs font-mono text-on-surface outline-none focus:border-primary"
                        />
                      </td>
                      <td className="px-3.5 py-2">
                        <input
                          type="number"
                          step="1"
                          min="0"
                          value={slab.amt_to}
                          onChange={(e) => updateSlab(idx, 'amt_to', Number(e.target.value))}
                          placeholder="0 for &infin;"
                          className="h-8 w-full rounded-md border border-outline-variant/30 bg-surface-container-low px-2.5 text-xs font-mono text-on-surface outline-none focus:border-primary"
                        />
                      </td>
                      <td className="w-40 px-3.5 py-2">
                        <AppDropdown
                          options={SLAB_TYPE_OPTIONS}
                          value={slab.slab_type}
                          onChange={(val) => updateSlab(idx, 'slab_type', Number(val))}
                          triggerClassName="h-8 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-md px-2"
                        />
                      </td>
                      <td className="px-3.5 py-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={slab.value}
                          onChange={(e) => updateSlab(idx, 'value', Number(e.target.value))}
                          className="h-8 w-full rounded-md border border-outline-variant/30 bg-surface-container-low px-2.5 text-xs font-mono font-bold text-on-surface outline-none focus:border-primary"
                        />
                      </td>
                      <td className="w-16 px-3.5 py-2 text-right">
                        <button
                          type="button"
                          onClick={() => removeSlab(idx)}
                          className="p-1 text-on-surface-variant hover:text-error transition rounded hover:bg-error/10"
                        >
                          <span className="material-symbols-outlined text-base">delete</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
